import moment from 'moment'
import * as R from 'ramda'
import { formatDate, fromCents } from 'src/utils'
import { HIDE_CONFIRM_DIALOG } from 'src/vuex/modules/confirm-dialog/types'
import { NOTIFY_ERROR, NOTIFY_SUCCESS } from 'src/vuex/modules/snackbar/types'
import { SPINNER_TYPES } from 'src/vuex/types'
import BankAccounts from '../../companies/components/company-accounts/bank-accounts/bank-accounts.vue'
import * as apiCalls from '../apiCalls'
import texts from '../texts.json'
import { matchSettleValues, settlementError } from './helpers'
import * as types from './types'

export default {
  // MATCHING TABLE
  // Get Matching
  [types.GET_MATCHING]: async ({ commit, getters, dispatch, state }) => {
    commit(types.SET_DEFAULT_VALUES)
    commit(types.SET_IS_LOADING, true)

    try {
      const params = getters[types.PARAMS_TO_SEND]
      const matching = await apiCalls.getIncomingPayments(
        params,
        state.selectedTab
      )

      commit(types.SET_INCOMING_PAYMENTS, matching.payments)
      commit(types.SET_TOTAL_ITEMS, matching.pagination.total_items)
    } catch (err) {
      console.error(err)
      dispatch(NOTIFY_ERROR, { message: err.response.data })

      commit(types.SET_INCOMING_PAYMENTS, [])
      commit(types.SET_TOTAL_ITEMS, 0)
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  },
  // Matching pagination
  [types.CHANGE_PAGINATION]({ commit }, newPagination) {
    commit(types.SET_PAGINATION, newPagination)
  },
  [types.RESET_PAGINATION]({ state, dispatch }) {
    dispatch(types.CHANGE_PAGINATION, {
      ...state.pagination,
      page: 1
    })
  },
  // Matching filters
  [types.CHANGE_FILTERS]({ commit }, { key, value }) {
    commit(types.SET_FILTERS, {
      key,
      value
    })
  },
  // Matching actions
  [types.UPDATE_MATCHING_ACTIONS]({ commit }, { key, value, id }) {
    commit(types.SET_MATCHING_ACTION, {
      key,
      value,
      id
    })
  },
  [types.CHANGE_SELECTED_TAB]({ commit }, newSelectedTab) {
    commit(types.SET_SELECTED_TAB, newSelectedTab)
  },
  [types.MARK_AS_RESOLVED]: async (
    { state, commit, dispatch },
    { comment }
  ) => {
    commit(types.SET_IS_LOADING, true)

    try {
      await apiCalls.resolvePayment(state.actions.id, { comment })

      dispatch(NOTIFY_SUCCESS, {
        message: texts.notifications.success.resolved
      })
      dispatch(types.GET_MATCHING)
      commit(types.SET_MATCHING_ACTION, {
        key: 'markedAsResolved',
        value: false
      })
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err.response.data })
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  },
  [types.MARK_AS_UNMATCHED]: async (
    { state, commit, dispatch },
    { comment }
  ) => {
    commit(types.SET_IS_LOADING, true)

    try {
      await apiCalls.unmatchPayment(state.actions.id, { comment })

      dispatch(NOTIFY_SUCCESS, {
        message: texts.notifications.success.unmatched
      })
      dispatch(types.GET_MATCHING)
      commit(types.SET_MATCHING_ACTION, {
        key: 'markedAsUnmatched',
        value: false
      })
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err.response.data })
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  },
  // Matching timeline
  [types.UPDATE_TIMELINE]({ commit, state }, { open, id }) {
    const matching = state.matchingData
    const pos = matching.findIndex((item) => item.id === id)
    const comments = pos !== -1 ? matching[pos].comments : []

    commit(types.SET_TIMELINE_VALUE, {
      open,
      id,
      comments: comments ? comments : []
    })
  },
  // Matching timeline comment
  [types.COMMENT_INCOMING_PAYMENT]: async ({ state, dispatch }, comment) => {
    try {
      dispatch(SPINNER_TYPES.SPINNER.SHOW)
      const params = {
        incomingPaymentId: state.timeline.id,
        comment
      }
      await apiCalls.commentIncomingPayment(params)

      dispatch(NOTIFY_SUCCESS, { message: 'Comment sent' })
      await dispatch(types.GET_MATCHING)
      dispatch(types.UPDATE_TIMELINE, {
        open: true,
        id: state.timeline.id
      })
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err.response.data })
    } finally {
      dispatch(SPINNER_TYPES.SPINNER.HIDE)
    }
  },
  // SELECTED TO SEND
  [types.UPDATE_SELECTED_TO_SEND](
    { commit },
    { amount, filters, beneficiaryInfo }
  ) {
    const newSelectedToSend = {
      amount,
      reference: filters.reference,
      beneficiaryName: filters.beneficiary_name,
      companyID: beneficiaryInfo.companyID,
      type: beneficiaryInfo.type,
      accountDetail1: beneficiaryInfo.accountDetail1,
      accountDetail2: beneficiaryInfo.accountDetail2,
      isEmpty: false
    }
    commit(types.SET_SELECTED_TO_SEND, newSelectedToSend)
  },
  [types.CLEAR_SELECTED_TO_SEND]({ commit }) {
    commit(types.SET_SELECTED_TO_SEND, { isEmpty: true })
  },
  [types.SEND_PAYMENT]: async ({ commit, dispatch }, { id, data }) => {
    commit(types.SET_IS_LOADING, true)
    try {
      await apiCalls.sendPayment(id, data)
      commit(types.SET_PAYMENT_RESULT, {
        key: 'forwardSuccess',
        value: true
      })
      commit(types.SET_SUCCESS_MESSAGE, true)
      dispatch(types.CLEAR_SELECTED_TO_SEND)
      commit(types.SET_BENEFICIARY_INFO, [])
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err.response.data })
      commit(types.SET_PAYMENT_RESULT, {
        key: 'actionFailed',
        value: true
      })
      commit(types.SET_SUCCESS_MESSAGE, false)
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  },
  [types.GET_BENEFICIARY_INFO]: async ({ commit, dispatch }, params) => {
    commit(types.SET_IS_LOADING, true)
    try {
      const response = await apiCalls.getCompanies(params)
      dispatch(types.GET_BANK_ACCOUNTS, response)
    } catch (err) {
      dispatch(NOTIFY_ERROR, {
        message:
          'There was an error getting beneficiary Info. Contact Tech team.'
      })
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  },
  [types.GET_BANK_ACCOUNTS]({ commit, dispatch, state }, companies) {
    companies.forEach(async (company) => {
      const res = await apiCalls.getBankAccounts(company.id)
      if (res.company == null || res.company.length < 1) {
        dispatch(NOTIFY_ERROR, {
          message: 'Bank accounts not found for companyID ' + company.id
        })
        return
      }
      const bankAccount = res.company[0]
      let info = {}
      info.companyID = company.id
      info.name = company.name

      info.crn = company.country_code + company.country_registration_number
      info.state = company.is_suspended ? 'DISABLED' : 'ACTIVE'
      info.currency = bankAccount.currency
      switch (info.currency) {
        case 'GBP':
          info.accountDetail1 = bankAccount.sort_code
          info.accountDetail2 = bankAccount.account_number
          break
        case 'EUR':
          info.accountDetail1 = bankAccount.iban
          info.accountDetail2 = bankAccount.bic
          break
      }
      const newBeneficiaryInfo = [...state.beneficiaryInfo, info]
      commit(types.SET_BENEFICIARY_INFO, newBeneficiaryInfo)
    })
  },
  // SETTLE
  [types.GET_SELECTED_SETTLEMENT]({ commit }, data) {
    commit(types.SET_SELECTED_SETTLEMENT, data)
  },
  [types.GET_SETTLE_PAYMENT_INFO]: async ({ commit, dispatch }, id) => {
    try {
      const paymentResponse = await apiCalls.getIncomingPayment(id)
      const forwardPaymentResponse = await apiCalls.getForwardPayments(id)

      commit(types.SET_SETTLE_PAYMENT_INFO, {
        paymentInfo: paymentResponse.payment,
        forwardPayments: forwardPaymentResponse.forwardPayments ?? []
      })
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err.response.data })
    }
  },
  [types.ADD_TO_SETTLE]({ state, dispatch, commit }, { data, product }) {
    const amountToDiscount = () => {
      return data.reduce((acc, item) => {
        if (item.invoiceData.amount) return acc + item.invoiceData.amount
        return acc
      }, 0)
    }
    commit(types.SET_AMOUNT_TO_DISCOUNT, amountToDiscount)
    const incomingPayment = state.settle.paymentInfo.remainingAmount
    const remainingAmount = incomingPayment - amountToDiscount
    let type = ''
    let invoiceData = {}

    invoiceData.id = data.data.id
    remainingAmount < data.amountPendingToSettle
      ? (invoiceData.amount = remainingAmount)
      : (invoiceData.amount = data.amountPendingToSettle)
    remainingAmount < data.amountPendingToSettle
      ? (type = 'Partial')
      : (type = 'Full')
    invoiceData.date = moment().format('YYYY/MM/DD')

    if (data.data.payment_reference) {
      invoiceData.reference = data.data.payment_reference
    } else if (data.data.payment_reference_number) {
      invoiceData.reference = data.data.payment_reference_number
    }

    if (product === 'ETR') {
      invoiceData.useDeposit = false
    }

    dispatch(types.PUSH_SELECTED_TO_SETTLE, {
      type: type,
      invoiceData: invoiceData,
      etcId: data.etcId
    })
  },
  [types.PUSH_SELECTED_TO_SETTLE]({ commit, state, dispatch }, data) {
    const newSelectedtoSettle = [...state.selectedToSettle, data]
    commit(types.SET_SELECTED_TO_SETTLE, newSelectedtoSettle)
  },
  // Delete selected settle
  [types.DELETE_STATEMENT]({ commit }, id) {
    commit(types.SET_STATEMENT_TO_DELETE, id)
  },
  [types.SPLICE_SELECTED_TO_SETTLE]({ commit, state, dispatch }, id) {
    const newSelectedtoSettle = [...state.selectedToSettle]
    const indexOfDeletedStatement = state.selectedToSettle.findIndex(
      (statement) => statement.invoiceData.id === id
    )
    newSelectedtoSettle.splice(indexOfDeletedStatement, 1)

    commit(types.SET_SELECTED_TO_SETTLE, newSelectedtoSettle)
  },
  [types.CHANGE_SETTLE_USE_DEPOSIT](
    { commit, state },
    { invoiceId, useDeposit }
  ) {
    const newSelectedtoSettle = R.clone(state.selectedToSettle)
    newSelectedtoSettle.forEach((item) => {
      if (item.invoiceData.id === invoiceId) {
        item.invoiceData.useDeposit = useDeposit
      }
    })
    commit(types.SET_SELECTED_TO_SETTLE, newSelectedtoSettle)
  },
  // Settle selected invoices
  [types.SEND_SETTLED_INVOICES]: async (
    { commit, dispatch, state },
    { product, paymentId }
  ) => {
    commit(types.SET_IS_LOADING, true)
    try {
      if (product === 'ETR') {
        let selected = R.clone(state.selectedToSettle)
        const dataToSend = matchSettleValues(
          state.newSettleAmountValue,
          selected
        )
        const invoicesToSettle = dataToSend.map((item) => {
          return item.invoiceData
        })

        await apiCalls.settleInvoices(paymentId, invoicesToSettle)
        commit(types.SET_PAYMENT_RESULT, {
          key: 'forwardSuccess',
          value: true
        })
        commit(types.SET_SUCCESS_MESSAGE, true)
      }
      dispatch(NOTIFY_SUCCESS, {
        message: 'Payment sent correctly'
      })
    } catch (err) {
      let selected = R.clone(state.selectedToSettle)

      dispatch(NOTIFY_ERROR, {
        message: err.response
          ? settlementError(err.response.data.message, selected)
          : err
      })
      commit(types.SET_PAYMENT_RESULT, {
        key: 'actionFailed',
        value: true
      })
      commit(types.SET_SUCCESS_MESSAGE, false)
    } finally {
      commit(types.RESET_SETTLE_VALUES)
      commit(types.SET_IS_LOADING, false)
      commit(HIDE_CONFIRM_DIALOG)
    }
  },
  [types.SEND_SETTLED_PAYABLES]: async (
    { state, commit, dispatch },
    { paymentId }
  ) => {
    try {
      const etpsToSettle = state.selectedToSettleEtp.map((selectedETP) => {
        return {
          id: selectedETP.id,
          reference: state.settle.paymentInfo.reference,
          amount: selectedETP.amount,
          date: formatDate(new Date(), 'YYYY/MM/DD')
        }
      })

      await apiCalls.settleETPs(paymentId, etpsToSettle)

      commit(types.SET_PAYMENT_RESULT, {
        key: 'forwardSuccess',
        value: true
      })
      commit(types.SET_SUCCESS_MESSAGE, true)
      dispatch(NOTIFY_SUCCESS, {
        message: 'Payment sent correctly'
      })
    } catch (err) {
      dispatch(NOTIFY_ERROR, { message: err })
      commit(types.SET_PAYMENT_RESULT, {
        key: 'actionFailed',
        value: true
      })
      commit(types.SET_SUCCESS_MESSAGE, false)
    } finally {
      commit(types.RESET_SETTLE_VALUES)
      commit(types.SET_IS_LOADING, false)
      commit(HIDE_CONFIRM_DIALOG)
    }
  },
  // Settle results
  [types.SORT_RESULTS_BY_STATUS]({ commit }, results) {
    const states = [
      'PUBLISHED',
      'REJECTED',
      'WRITTEN_OFF',
      'SETTLED',
      'PRE_SETTLED',
      'CANCELLED',
      'APPROVED',
      'PENDING',
      'AVAILABLE',
      'RECEIVED',
      'TROUBLESHOOT'
    ]
    let sortedArray = []
    for (let state of states) {
      for (let item of results) {
        if (item.state === state) sortedArray.push(item)
      }
    }
    commit(types.SET_RESULTS_LIST, sortedArray)
  },
  [types.GET_LIST_RESULTS]: async (
    { commit, dispatch, state },
    { query, product }
  ) => {
    commit(types.SET_IS_LOADING, true)
    try {
      if (product === 'ETR') {
        const response = await apiCalls.getInvoicesSearchResults(query)
        const resultsWithIncomingPaymentCurrency = response.results.filter(
          (item) => {
            return item.currency === state.settle.paymentInfo.currency
          }
        )
        dispatch(
          types.SORT_RESULTS_BY_STATUS,
          resultsWithIncomingPaymentCurrency
        )
        commit(types.SET_SETTLE_PAGINATION_TOTAL, {
          ...state.settle.pagination,
          total: response.pagination.total_items
        })
      } else if (product === 'ETP') {
        const payableSearchResults = await apiCalls.getPayableSearchResults(
          state.settle.paymentInfo.id,
          query
        )

        const formattedPayables = payableSearchResults.results.reduce(
          (agg, item) => {
            if (item.currency === state.settle.paymentInfo.currency) {
              agg.push({
                ...item,
                amountFromCents: fromCents(item.amount)
              })
            }

            return agg
          },
          []
        )

        commit(types.SET_SEARCH_RESULTS_ETP, formattedPayables)
      }
    } catch (err) {
      console.error(err)
      dispatch(NOTIFY_ERROR, { message: err.response })
    } finally {
      commit(types.SET_IS_LOADING, false)
    }
  }
}
